import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, delay, fork, race, select, put, take, takeEvery } from 'redux-saga/effects';
import { RootState } from 'types';
import {
  IPlayerState,
  IViewer,
  IVideoState,
  updatePlayerState,
  updateVideoState,
  updateViewerState,
  VideoPlayState,
  ViewerState,
} from 'core/slices/player';
import { EntityTypes } from 'constants/entities';
import { ActionBehaviour, IActionBasic } from 'types/domain/action';
import { selectAction, selectActionVoiceSceneId } from 'core/selectors/product/action/action';
import { IUpdateState, updateState } from 'core/actions/product/state';
import { selectSceneAsset } from 'core/selectors/product/scene/sceneAsset';
import { selectSceneConfig, selectVoiceSceneConfig } from 'core/selectors/product/scene/sceneConfig';
import { ISceneConfig, ISceneDAO, SceneType } from 'types/domain/scene';
import { AssetType, IAsset } from 'types/app/asset';
import { loadAsset } from 'core/services/asset';
import { LoadingState } from 'types/domain/base';
import { dashPlayerEvents, VideoEvents, videoPlaybackEvents } from 'utils/videoEvents';
import { isIOS, isSafari } from 'react-device-detect';
import { selectScene } from 'core/selectors/product/scene/scene';
import { IActionFeed, selectActionFeed } from 'core/selectors/product/action/actionFeed';
import { selectActionEndTime } from 'core/selectors/product/action/actionEndTime';
import { selectActionEditor } from 'core/slices/actionEditor/selectActionEditor';
import { IActionEditorState } from 'core/slices/actionEditor';
import { loaders } from '../product/assets/load';
import { dashjs, dashStreamingSettings } from 'utils/loadDash';
import { unloadAsset } from 'core/slices/assets';

import { prev } from 'core/slices/register';
import { selectSessionMode } from 'core/selectors/admin/sessionMode';
import { Mode } from 'core/slices/session';

let DashPlayer = null;

// How long manageVideo waits with zero playback events before treating the
// element as wedged (see the watchdog in manageVideo). A healthy element floods
// the channel with `timeupdate` (~30ms) while playing, and any load/seek emits
// canplay/progress/seeked — so a full silent window only happens when dash has
// wedged (readyState parked, no events). Kept comfortably above one network
// round-trip so genuinely slow-but-progressing loads never trip it.
const READY_WATCHDOG_MS = 3000;
// Silent windows to nudge with a seek before escalating to a full source rebuild.
const READY_WATCHDOG_NUDGES = 2;
// Cap on full source rebuilds so a truly unplayable asset can't reload forever.
const READY_WATCHDOG_MAX_RELOADS = 2;

// Tracks the asset whose end-of-video relay we've already started, so the two
// independent end detectors (manageVideo's <video> events and dashEvents'
// playbackTimeUpdated) can't both pass the `!ended` check and double-fire the
// next-action transition. Reset whenever `ended` is set back to false (see
// removeVideo / startVideo) so a replayed or shared-scene video can end again.
let endedAssetId: string | null = null;

// True while a scene's video source is being (re)attached to the shared #videoEl.
// dash fires an `emptied` event during reattach even though the element keeps a
// valid source and plays; without this guard the EMPTIED handler nulls
// `video.assetId`, which flips selectSceneLoaded to false and hides the (correct,
// playing) video mesh → black. Cleared once the element reports it can play.
let attachingSource = false;

function* assetLoaded(action) {
  const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (
    sceneAsset &&
    action.meta.arg.id === sceneAsset.id &&
    sceneAsset.type === AssetType.VIDEO &&
    videoState.assetId !== action.meta.arg.id
  )
    yield loadSceneVideo(sceneAsset, 'assetLoaded');
}

function* loadSceneVideo(asset: IAsset, caller?: string, recoveryAttempt = 0) {
  if (asset.type !== AssetType.VIDEO) return console.warn('trying to load an asset that isnt video');

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  // A matching `assetId` is not proof the element still has a live source: after
  // returning from a no-video scene the dash source can be torn down (SourceBuffer
  // removed) while `assetId` stays set, so the normal guard would skip reattaching
  // and leave the texture black. Treat "no decodable source on the element" as a
  // mismatch too, forcing removeVideo → full reload.
  const hasLiveSource = !!videoEl && !!(videoEl.currentSrc || videoEl.src) && videoEl.readyState > 0;
  if (asset.id !== videoState.assetId || !hasLiveSource) yield removeVideo('initial load remove');

  yield put(yield call(updateVideoState, { assetId: asset.id, ready: false }));
  const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  // dash turns the `#t=start,end` fragment's `end` into a fixed SourceBuffer
  // append window. In the editor the end is being edited and the timeline scrubs
  // freely, so a baked-in end both goes stale on edit and blocks seeking past it
  // (→ "Failed to set append window" / byteLength append errors). Omit `end`
  // there and let the saga stop playback at the live edited end instead; the
  // player keeps the end for its exact, native stop.
  const actionEditor: IActionEditorState = yield select(selectActionEditor);
  const includeEnd = !actionEditor?.enabled && config?.end;
  const hasStartOrEnd = config?.start || config?.end;
  const tFragment = hasStartOrEnd ? `#t=${config?.start ?? 0}${includeEnd ? `,${config.end}` : ''}` : '';
  const configuredUrl = `${asset.url}${tFragment}`;

  // Guard the upcoming source (re)attach: dash emits `emptied` mid-attach, which
  // must not be treated as an unload (see EMPTIED handler in manageVideo).
  attachingSource = true;

  if (isIOS || isSafari) {
    videoEl.src = configuredUrl;
    videoEl.load();
  } else yield fork(loadDash, asset, configuredUrl);

  //mute while managing
  videoEl.muted = true;

  yield manageVideo(asset, recoveryAttempt);
}

function* loadDash(asset: IAsset, url) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  while (asset.loading !== LoadingState.LOADED) {
    yield take(loadAsset.fulfilled);
    // eslint-disable-next-line no-loop-func
    asset = yield select((state: RootState) => state.core.assets[asset.id]);
  }

  if (loaders[asset.id]) {
    if (!loaders[asset.id].attachView) return; //console.warn('no attachView for', asset.id);

    // Reattaching a preloaded dash player to the shared #videoEl leaves its segment
    // scheduler stale: on a later switch dash can't generate a media request for the
    // start segment and falls back to seeking near the end (readyState stuck at 1 →
    // black, "Adjusting playback time … because of gap in the manifest"). Calling
    // attachView + attachSource alone does NOT clear that state — the player is
    // fresh on first play, so it works once and wedges on every revisit.
    //
    // reset() tears the streaming stack down properly; attachView + attachSource
    // then rebuild a clean scheduler for this element. (attachView must come first
    // so the source binds to #videoEl.)
    if (loaders[asset.id].reset) yield call([loaders[asset.id], loaders[asset.id].reset]);

    // reset() also drops the player's settings back to dash's defaults, so the
    // creation-time settings must be re-applied here, before re-attaching — the
    // shared object is the single source of truth for what those are (see
    // dashStreamingSettings for why each one matters).
    yield call([loaders[asset.id], loaders[asset.id].updateSettings], dashStreamingSettings);

    yield call(loaders[asset.id].attachView, videoEl);
    yield call([loaders[asset.id], loaders[asset.id].attachSource], url);
    const mode: Mode = yield select(selectSessionMode);
    if (loaders[asset.id].isReady() && mode === Mode.ADMIN) yield startVideo();
    return;
  }

  if (DashPlayer == null) {
    const dash = yield call(dashjs);
    DashPlayer = yield call(dash.MediaPlayer().create);
    yield call([DashPlayer, DashPlayer.updateSettings], dashStreamingSettings);
    yield fork(dashEvents, DashPlayer, dash.MediaPlayer);
    if (videoEl) yield call([DashPlayer, DashPlayer.initialize], videoEl, null, true);
  }

  try {
    yield call([DashPlayer, DashPlayer.attachSource], url);
  } catch (e) {}
}
function* dashEvents(DashPlayer, MediaPlayer) {
  const videoEvents = yield call(dashPlayerEvents, DashPlayer, MediaPlayer);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;

  while (aId === assetId) {
    let event: Event = yield take(videoEvents);

    aId = yield select((state: RootState) => state.core.player.state.video.assetId);

    if (event.type === 'error') {
      yield put(yield call(updateVideoState, { play: VideoPlayState.ERROR }));
      yield put(yield call(updatePlayerState, { paused: true, culprit: 'dashError' }));
    }

    if (event.type === 'streamTeardownComplete') {
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
      if (sceneAsset.fallback && videoState.play === VideoPlayState.ERROR) {
        const config: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
        const configuredUrl = config?.start ? `${sceneAsset.fallback}#t=${config.start}` : sceneAsset.fallback;
        yield call([DashPlayer, DashPlayer.attachSource], configuredUrl);
      }
    }

    if (event.type === 'playbackTimeUpdated') {
      // Gate on `ready` for the same reason as manageVideo's end-detection: until
      // startVideo() has positioned the new source, dash's time() can still report
      // the previous video's playhead, which on a shorter `end` would fire a false
      // endVideo() → the freshly-attached slide is skipped. A real end only ever
      // arrives while ready+playing.
      const { ended, ready } = yield select((state: RootState) => state.core.player.state.video);
      if (!ended && ready) {
        const actionEndTime: number | null = yield select(state => selectActionEndTime(state, undefined));
        if (actionEndTime != null && DashPlayer.time() >= actionEndTime) yield endVideo();
      }
    }
  }
  // Critical: Close the event channel to remove all event listeners
  videoEvents.close();
}

export function* removeVideo(cause = null) {
  //console.warn('removeVideo', cause);

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (videoState.assetId) yield put(yield call(unloadAsset, videoState.assetId));

  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;

  if (DashPlayer && DashPlayer.isReady()) {
    DashPlayer.pause();
  } else if ((isIOS || isSafari) && videoEl.src) {
    videoEl.src = '';
    videoEl.load();
  }
  endedAssetId = null;
  yield put(yield call(updateVideoState, { assetId: null, play: VideoPlayState.PAUSED, ready: false, ended: false }));
}

function* updatePlayerStateSaga(action: PayloadAction<Partial<IPlayerState>>) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoState: VideoPlayState = yield select((state: RootState) => state.core.player.state.video.play);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const config = voiceSceneConfig ?? sceneConfig;

  const actionVoiceSceneId = yield select(state => selectActionVoiceSceneId(state, undefined));

  if (action.payload.paused === undefined || !config || (!scene && !actionVoiceSceneId)) return;
  // console.warn('cannot play 1', {
  //   paused: action.payload.paused,
  //   noConfig: !config,
  //   noScene: !scene && !actionVoiceSceneId,
  // });

  if (!action.payload.paused) {
    if (
      (!actionVoiceSceneId && scene.type !== SceneType.VIDEO) ||
      !videoEl.src ||
      !videoEl.src.length ||
      Math.abs(videoEl.currentTime - (config.end || videoEl.duration)) < 0.1
    )
      return;
    // console.warn('cannot play 2', {
    //   invalidSceneType: !actionVoiceSceneId && scene.type !== SceneType.VIDEO,
    //   noSrc: !videoEl.src,
    //   noSrcLength: !videoEl.src.length,
    //   nearEnd: Math.abs(videoEl.currentTime - (config.end || videoEl.duration)) < 0.1,
    // });
  }

  try {
    if (
      ![VideoPlayState.PAUSED, VideoPlayState.START_PAUSING].includes(videoState) &&
      action.payload.paused &&
      !videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PAUSING }));
      yield videoEl.pause();
    }
    if (
      ![VideoPlayState.PLAYING, VideoPlayState.START_PLAYING].includes(videoState) &&
      action.payload.paused === false &&
      videoEl.paused
    ) {
      yield put(yield call(updateVideoState, { play: VideoPlayState.START_PLAYING }));
      yield videoEl.play();
    }
  } catch (e) {}
}

function* manageVideo(asset?: IAsset, recoveryAttempt = 0) {
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const videoEvents = yield call(videoPlaybackEvents);
  const assetId = yield select((state: RootState) => state.core.player.state.video.assetId);
  let aId = assetId;

  // Counts consecutive fully-silent watchdog windows (no playback event at all).
  // Any real event resets it, so it only climbs while the element is wedged.
  let staleWindows = 0;

  // `ready` is only ever set by startVideo(), and the loop below can only call it
  // in response to a canplay/progress/seeked event. On a *revisit* the asset is
  // already LOADED, so loadDash's wait loop never blocks and it runs
  // attachView/attachSource synchronously — before this channel exists. Those
  // events fire into the void, the element then parks at readyState 4 with
  // nothing left to emit, and `ready` stays false forever → selectProceed pins on
  // LOADING (the stuck spinner going back-then-forward; the first visit works
  // only because the asset load yields long enough for the channel to be up).
  // So don't wait for an event that already happened: if the element is already
  // playable, kick startVideo now. Doing it here rather than in loadDash means
  // any seek it triggers is observed by the loop below.
  const { ready: alreadyReady } = yield select((state: RootState) => state.core.player.state.video);

  if (!alreadyReady && videoEl && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) yield startVideo();

  while (assetId === aId) {
    // Race the next playback event against a watchdog timer. `ready` is only ever
    // set by startVideo(), which the handlers below reach in response to a
    // canplay/progress/seeked event. dash can wedge on a preloaded-player reattach
    // — readyState parks at 1 and it emits *no* event — so the loop would block on
    // `take` forever, `ready` stays false, and selectProceed pins the next button
    // on LOADING permanently (learners can't advance; admins bypass that gate).
    // The watchdog gives us a way to notice "nothing is happening and we're not
    // ready" and force recovery instead of blocking indefinitely.
    const { event, watchdog }: { event?: Event; watchdog?: true } = yield race({
      event: take(videoEvents),
      watchdog: delay(READY_WATCHDOG_MS),
    });

    if (watchdog) {
      const wedged: IVideoState = yield select((state: RootState) => state.core.player.state.video);
      // Mirror the event branch: track the live assetId so a scene swap ends this
      // loop via the `while` guard instead of us managing a video we no longer own.
      aId = wedged.assetId;
      // Asset swapped out from under us, or it became ready on its own — stand down.
      if (assetId !== aId || wedged.ready) {
        staleWindows = 0;
        continue;
      }

      // Only ever recover the video belonging to the CURRENT action. While a slide
      // transitions, the outgoing scene's source is deliberately left attached so
      // its last frame stays visible (see the deferred teardown in updateStateSaga
      // / viewerRestSaga). Nudging that stale video back to life could drive it to
      // its end and fire a spurious endVideo → next, skipping the slide the learner
      // just landed on — most visible on image actions, which have no video of
      // their own for the watchdog to be about.
      const currentAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      if (!currentAsset || currentAsset.id !== aId) {
        staleWindows = 0;
        continue;
      }

      staleWindows += 1;

      // First few silent windows: nudge with startVideo(). With metadata present
      // (readyState >= 1) it seeks to `start`, which forces dash to request the
      // start segment and unwedges the stalled scheduler; if the element is
      // already playable it just flips `ready` true.
      if (staleWindows <= READY_WATCHDOG_NUDGES && videoEl.readyState >= HTMLMediaElement.HAVE_METADATA) {
        console.warn(
          `[video watchdog] nudging wedged video: asset=${aId} readyState=${videoEl.readyState} ` +
            `networkState=${videoEl.networkState} currentTime=${videoEl.currentTime.toFixed(2)} ` +
            `window=${staleWindows}/${READY_WATCHDOG_NUDGES} reload=${recoveryAttempt}`,
        );
        yield startVideo();
        continue;
      }

      // Persistent wedge (no decodable data, or nudges didn't take): rebuild the
      // source from scratch, bounded so an unplayable asset can't loop forever.
      if (asset && recoveryAttempt < READY_WATCHDOG_MAX_RELOADS) {
        console.warn(
          `[video watchdog] rebuilding wedged video source: asset=${aId} readyState=${videoEl.readyState} ` +
            `networkState=${videoEl.networkState} mediaError=${videoEl.error?.code ?? 'none'} ` +
            `reload=${recoveryAttempt + 1}/${READY_WATCHDOG_MAX_RELOADS}`,
        );
        videoEvents.close();
        yield removeVideo('ready watchdog - wedged reload');
        yield loadSceneVideo(asset, 'ready watchdog', recoveryAttempt + 1);
        return;
      }
      // Exhausted recovery — the learner is likely stuck. Loud, searchable marker
      // that pairs with the video diagnostics in any report captured from here.
      console.error(
        `[video watchdog] gave up on wedged video: asset=${aId} readyState=${videoEl.readyState} ` +
          `networkState=${videoEl.networkState} mediaError=${videoEl.error?.code ?? 'none'} ` +
          `after ${recoveryAttempt} reload(s)`,
      );
      continue;
    }

    staleWindows = 0;
    if (!event) continue;

    const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
    aId = videoState.assetId;

    // The (re)attach is finished once the element reports it can play — from here
    // an `emptied` genuinely means the source was lost, not a dash reattach.
    if (
      [VideoEvents.PLAYING, VideoEvents.CANPLAY, VideoEvents.CANPLAYTHROUGH, VideoEvents.LOADEDDATA].includes(
        event.type as VideoEvents,
      )
    )
      attachingSource = false;

    switch (event.type) {
      case VideoEvents.PLAYING:
        if (![VideoPlayState.PLAYING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PLAYING, buffering: false, seeking: false }));
        break;
      case VideoEvents.PAUSE:
        if (![VideoPlayState.PAUSED].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.PAUSED }));
        break;
      case VideoEvents.WAITING:
        if (![VideoPlayState.BUFFERING].includes(videoState.play))
          yield put(yield call(updateVideoState, { play: VideoPlayState.BUFFERING, buffering: true }));
        break;
      case VideoEvents.EMPTIED:
        if (
          !attachingSource &&
          ![VideoPlayState.SEEKING, VideoPlayState.BUFFERING].includes(videoState.play) &&
          videoState.ready
        )
          if (videoState.assetId)
            yield put(
              yield call(updateVideoState, {
                play: VideoPlayState.EMPTIED,
                assetId: null,
                buffering: false,
                seeking: false,
              }),
            );
        break;
    }

    const { ready } = yield select((state: RootState) => state.core.player.state.video);

    // startVideo() is the only place `ready: true` is set. This used to demand
    // readyState === 4 (HAVE_ENOUGH_DATA) *and* one of four specific event types.
    // dash streams with manifest gaps ("Adjusting playback time … gap in the
    // manifest"), which parks the element at readyState 3 (HAVE_FUTURE_DATA —
    // playable, but no promise of 4). It's perfectly playable, just never that one
    // exact value, so startVideo was never called at all: `ready` stayed false and
    // selectProceed pinned the next button on LOADING forever.
    //
    // Any event is a fine moment to ask "is it playable yet?" — HAVE_FUTURE_DATA
    // is the standard "can play forward from here" threshold, and startVideo does
    // the rest (seeks to `start` and only flips `ready` once positioned).
    if (!ready && videoEl.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) yield startVideo();

    // Re-read after startVideo() — it may have just flipped `ready` true (element
    // already at `start`) or kicked a seek that leaves `ready` false (any other
    // position). We gate end-detection on the *current* value, not the pre-call one.
    const { ended, ready: readyNow } = yield select((state: RootState) => state.core.player.state.video);

    // A video can only legitimately end once it is `ready` — i.e. startVideo() has
    // confirmed it's positioned at `start` and playable. Until then the shared
    // #videoEl, mid source-switch, briefly reports the PREVIOUS video's
    // currentTime/duration; on an action whose `end` is smaller than that stale
    // position, end-detection would fire a false endVideo() → next → the new slide
    // is skipped the instant its video attaches. Gating on `ready` closes that
    // window (a real end always happens while ready+playing); the position/seek
    // guards below remain as a second line of defence.
    if (!ended && readyNow) {
      if ([VideoEvents.ENDED].includes(event.type as VideoEvents)) yield endVideo();

      const actionEndTime: number | null = yield select(state => selectActionEndTime(state, undefined));
      const end = actionEndTime ?? videoEl.duration;

      // Only treat currentTime >= end as "ended" when the position is trustworthy.
      // While switching sources (e.g. returning to a video via an action tile) the
      // element is mid-seek and briefly reports a stale currentTime carried over
      // from the previous video; if that stale value is past this action's `end`
      // we fire a false endVideo() → in admin the player pauses on the black intro
      // frame. Skip the check while seeking or when currentTime is outside the
      // current source's valid duration.
      const duration = videoEl.duration;
      const positionIsReliable =
        !videoEl.seeking && Number.isFinite(duration) && videoEl.currentTime <= duration + 0.25;

      if (Number.isFinite(end) && end > 0 && positionIsReliable && videoEl.currentTime >= end) yield endVideo();
    }
  }

  videoEvents.close();
}

function* startVideo(newVideo = true) {
  const { ready } = yield select((state: RootState) => state.core.player.state.video);
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  const config = action?.voiceSceneConfigId ? voiceSceneConfig : sceneConfig;

  if (!config) return console.warn('cant play video - no config');

  const start = config.start || 0.1;

  if (!videoEl) return console.warn('cant play video - no video');

  if (videoState.ended) {
    endedAssetId = null;
    yield put(yield call(updateVideoState, { ended: false }));
  }

  if (Math.abs(videoEl.currentTime - start) < 0.1) {
    // `newVideo` gates the pause, not the readiness flag. This is the only place
    // `ready: true` is ever set, and the revisit path (updateStateSaga, ACTION,
    // same scene already attached) calls startVideo(false) — so with the element
    // already sitting at `start` this branch used to set nothing at all. Combined
    // with loadSceneVideo's unconditional `ready: false`, that left `ready` stuck
    // false forever and selectProceed pinned on LOADING (the spinner on a video
    // you've already visited). A video positioned at `start` and playable is
    // ready no matter how we got here.
    if (!ready) {
      if (newVideo) yield put(yield call(updatePlayerState, { paused: true, cuprit: 1 }));
      yield put(yield call(updateVideoState, { ready: true }));
    }
  } else {
    if (ready) yield put(yield call(updateVideoState, { ready: false }));

    yield put(yield call(updateVideoState, { play: VideoPlayState.SEEKING, seeking: true }));

    videoEl.currentTime = start;
    return;
  }

  if (videoEl.volume !== config.volume || videoEl.muted) {
    //if (videoState.interacted)
    videoEl.muted = config.volume === 0;
    videoEl.volume = config.volume || 1;
  } else videoEl.muted = false;
}

function* endVideo() {
  const sceneConfig: ISceneConfig = yield select(state => selectSceneConfig(state, undefined, undefined));
  const voiceSceneConfig: ISceneConfig = yield select(state => selectVoiceSceneConfig(state, undefined, undefined));
  const config = voiceSceneConfig ?? sceneConfig;
  const videoEl: HTMLVideoElement = document.getElementById('videoEl') as HTMLVideoElement;
  const action: IActionBasic = yield select(state => selectAction(state, undefined));

  if (config && config.loop) {
    const start = config.start || 0;
    videoEl.currentTime = start;
    return;
  }
  if (action && action.behaviour === ActionBehaviour.PAUSE && !action.voiceSceneId) return;

  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  // Idempotency guard: bail if this asset's end has already been handled, even if
  // the `ended` flag hasn't committed yet (concurrent end detectors race here).
  if (videoState.ended || (videoState.assetId != null && endedAssetId === videoState.assetId)) return;
  endedAssetId = videoState.assetId;

  yield put(yield call(updateVideoState, { ended: true }));
}

function* updateStateSaga(action: PayloadAction<Partial<IUpdateState>>) {
  // const ignore = yield select(selectIgnoreActions);
  // if (ignore) return;

  switch (action.payload.type) {
    case EntityTypes.SCENE:
      let sceneAsset: IAsset = yield select(state => selectSceneAsset(state, action.payload.id));

      if (sceneAsset && sceneAsset.type === AssetType.VIDEO) yield loadSceneVideo(sceneAsset);

      break;
    case EntityTypes.ACTION: {
      const sceneAsset: IAsset = yield select(state => selectSceneAsset(state, undefined));
      const action: IActionBasic = yield select(state => selectAction(state, undefined));
      const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
      const hasVideo = (scene && scene.type === SceneType.VIDEO) || action?.voiceSceneId;

      if (action?.voiceSceneId) {
        const voiceSceneAsset = yield select(state => selectSceneAsset(state, action.voiceSceneId));
        if (voiceSceneAsset && voiceSceneAsset.type === AssetType.VIDEO) {
          yield loadSceneVideo(voiceSceneAsset);
        }
      } else if (!hasVideo && action && (!action.scene || action.scene.id !== sceneAsset?.entityId)) {
        // The SCENE pointer (and thus the video mesh) is held until the viewer
        // settles — see removeSceneIfNotRequired in module/scene.ts. Tearing the
        // video source down here (mid slide-transition) would black out the frozen
        // frame that's still visible behind the outgoing slide. Instead pause the
        // element to freeze the last frame and defer removeVideo to RESTING
        // (viewerRestSaga). Only tear down immediately if we're already at rest
        // (e.g. admin, no slide animation).
        const viewerState: ViewerState = yield select(
          (state: RootState) => state.core.player.state.viewer.state,
        );
        if (viewerState === ViewerState.RESTING) {
          yield removeVideo('action has no video');
        } else {
          yield put(yield call(updatePlayerState, { paused: true, culprit: 'defer video teardown' }));
        }
      }
      else if (hasVideo && action?.scene && scene.id === action.sceneId) {
        if (action.behaviour !== ActionBehaviour.PAUSE || action.voiceSceneId) yield startVideo(false);
        else yield put(yield call(updatePlayerState, { paused: true }));
      } else {
        //This is a general pause I had to put in for admin.
        const paused = yield select((state: RootState) => state.core.player.state.paused);
        if (!paused) yield put(yield call(updatePlayerState, { paused: true, culprit: 'video.updateStateSaga' }));
      }
    }
  }
}

// Deferred counterpart to the pause in updateStateSaga: once the slide transition
// finishes, if the now-current action has no video but a source is still attached
// (kept alive so its last frame stayed visible during the animation), tear it down.
function* viewerRestSaga(action: PayloadAction<IViewer>) {
  if (action.payload.state !== ViewerState.RESTING) return;

  // Base hasVideo on the settled action's own scene, not the SCENE pointer — the
  // pointer swap (scene.ts) and this saga both fire on RESTING and their order
  // isn't guaranteed. The current action is already the destination here.
  const currentAction: IActionBasic = yield select(state => selectAction(state, undefined));
  const hasVideo =
    (currentAction?.scene && currentAction.scene.type === SceneType.VIDEO) || currentAction?.voiceSceneId;
  const videoState: IVideoState = yield select((state: RootState) => state.core.player.state.video);

  if (!hasVideo && videoState.assetId) yield removeVideo('viewer rest - no video');
}

function* prevSaga() {
  const { index }: IActionFeed = yield select(selectActionFeed);
  const scene: ISceneDAO = yield select(state => selectScene(state, undefined));
  if (index === 0 && scene?.videoId) yield startVideo();
}

function* watchAssetLoaded() {
  yield takeEvery(loadAsset.fulfilled, assetLoaded);
}

function* watchPlayerState() {
  yield takeEvery(updatePlayerState.type, updatePlayerStateSaga);
}

function* watchUpdateState() {
  yield takeEvery(updateState.type, updateStateSaga);
}

function* watchPrev() {
  yield takeEvery(prev, prevSaga);
}

function* watchViewerRest() {
  yield takeEvery(updateViewerState.type, viewerRestSaga);
}

// Any explicit `ended: false` (e.g. the timeline editor seeking/resizing a
// region) means the video should be allowed to end again. Clear the dedupe
// guard so a subsequent end at the newly-edited end time isn't swallowed —
// without this, changing a video's end time and seeking leaves the guard
// pinned to the asset and the video plays past the new end.
function resetEndGuardSaga(action: PayloadAction<Partial<IVideoState>>) {
  if (action.payload.ended === false) endedAssetId = null;
}

function* watchResetEndGuard() {
  yield takeEvery(updateVideoState.type, resetEndGuardSaga);
}

export function* videoSaga() {
  yield all([
    fork(watchUpdateState),
    fork(watchPlayerState),
    fork(watchAssetLoaded),
    fork(watchPrev),
    fork(watchViewerRest),
    fork(watchResetEndGuard),
  ]);
}
